import { create } from 'zustand';
import { GetAbout, CreateAbout, UpdateAbout, DeleteAbout } from '../api/api';
import toast from 'react-hot-toast';

export interface About {
  _id?: string;
  title: string;
  content: string;
  image?: string;
  isDeleted?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface CreateAboutI {
  title?: string;
  content: string;
  image?: string;
}

interface UpdateAboutI {
  title?: string;
  content?: string;
  image?: string;
}

interface GetAboutResponse {
  data: {
    about: About;
  };
}

interface AboutResponse {
  data: {
    about: About;
  };
  status: boolean;
  message: string;
}

interface DeleteAboutResponse {
  status: boolean;
  message: string;
}

interface AboutStore {
  about: About | null;
  loading: boolean;
  error: string | null;
  getAbout: (isPublic?: boolean) => Promise<void>;
  createAbout: (about: CreateAboutI) => Promise<void>;
  updateAbout: (about: UpdateAboutI) => Promise<void>;
  deleteAbout: () => Promise<void>;
}

export const useAboutStore = create<AboutStore>((set, get) => ({
  about: null,
  loading: false,
  error: null,

  getAbout: async (isPublic?: boolean) => {
    set({ loading: true, error: null });
    const { data, error } = await GetAbout<GetAboutResponse>(isPublic);

    if (error) {
      set({ loading: false, error: error as string });
      return;
    }

    set({
      about: data.data.about || null,
      loading: false,
    });
  },

  createAbout: async (about: CreateAboutI) => {
    set({ loading: true, error: null });
    const { data, error } = await CreateAbout<AboutResponse>(about);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to create about page' });
      return;
    }

    set({ about: data.data.about, loading: false });
    toast.success('About page created successfully');
  },

  updateAbout: async (about: UpdateAboutI) => {
    set({ loading: true, error: null });
    const { data, error } = await UpdateAbout<AboutResponse>(about);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to update about page' });
      return;
    }

    set({ about: data.data.about, loading: false });
    toast.success('About page updated successfully');
  },

  deleteAbout: async () => {
    set({ loading: true, error: null });
    const { data, error } = await DeleteAbout<DeleteAboutResponse>();

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to delete about page' });
      return;
    }

    set({ about: null, loading: false });
    toast.success('About page deleted successfully');
  },
}));

export type { CreateAboutI, UpdateAboutI };
